/**
 * Vuex Store for Mailboxes
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2018
 */

import { MailboxesApi } from '@/services/api-mailboxes.js';

function defaultStateFactory() {
  return {
    mailboxes: [],
    pagination: {},
    mailbox: {
      attributes: {},
    },
  };
}

export const state = defaultStateFactory();

export const getters = {
  mailboxById: (state) => (id) => state.mailboxes.find((mailbox) => mailbox.id === id),
  mailboxIndex: (state) => (id) => state.mailboxes.findIndex((mailbox) => mailbox.id === id),
};

export const mutations = {
  setMailboxes(state, { meta = {}, data = [] }) {
    state.pagination = meta;
    state.mailboxes = data;
  },

  replaceMailboxInList(state, { mailbox = {}, index }) {
    state.mailboxes[index] = mailbox;
  },

  setMailbox(state, mailbox = {}) {
    state.mailbox = mailbox;
  },

  resetMailbox(state) {
    state.mailbox = {
      attributes: {},
    };
  },

  addMailbox(state, mailbox = {}) {
    state.mailboxes.unshift(mailbox);
  },

  destroyMailbox(state, { index }) {
    if (index !== -1) {
      state.mailboxes.splice(index, 1);
    }
  },

  resetState(state, defaultState) {
    state = defaultState;
  },
};

export const actions = {
  fetch({ commit, state }, { query }) {
    return MailboxesApi.index(query)
      .then((mailboxes) => {
        commit('setMailboxes', mailboxes);
        return state.mailboxes;
      })
      .catch((err) => {
        commit('setMailboxes', {});
        throw err;
      });
  },

  create({ commit }, { data }) {
    return MailboxesApi.store(data).then((mailbox) => {
      commit('addMailbox', mailbox);
      return mailbox;
    });
  },

  read({ commit, getters, state }, { id }) {
    // 1. Check if we've already fetched this mailbox as part of the list
    const mailbox = getters.mailboxById(id);
    if (mailbox) {
      commit('setMailbox', mailbox);
      return Promise.resolve(mailbox);
    }

    // 2. Fetch the mailbox from the API.
    return MailboxesApi.show(id)
      .then((mailbox) => {
        commit('setMailbox', mailbox);
        return state.mailbox;
      })
      .catch((err) => {
        commit('resetMailbox');
        throw err;
      });
  },

  update({ commit, getters, state }, { data, id }) {
    return MailboxesApi.update(id, data).then((mailbox) => {
      commit('setMailbox', mailbox);
      const mailboxIndex = getters.mailboxIndex(id);
      if (mailboxIndex !== -1) {
        commit('replaceMailboxInList', {
          mailbox: mailbox,
          index: mailboxIndex,
        });
      }

      return state.mailbox;
    });
  },

  destroy({ commit, getters }, { id }) {
    return MailboxesApi.destroy(id).then(() => {
      const mailboxIndex = getters.mailboxIndex(id);
      commit('destroyMailbox', { index: mailboxIndex });
    });
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('resetState', defaultStateFactory());
  },
};
